import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import Cropper from "cropperjs"

// Usage: data-controller="avatar-upload"
// Drives the avatar selection + in-browser crop flow on the settings form.
export default class extends Controller {
  static targets = [
    "fileInput",
    "preview",
    "initials",
    "removeFlag",
    "removeBtn",
    "modal",
    "cropperImage",
    "confirmBtn"
  ]
  static values = {
    initials: String,
    outputSize: { type: Number, default: 512 }
  }

  connect() {
    this._modal = new Modal(this.modalTarget)
    this._onModalHidden = this._teardownCropper.bind(this)
    this.modalTarget.addEventListener("hidden.bs.modal", this._onModalHidden)
  }

  disconnect() {
    this.modalTarget.removeEventListener("hidden.bs.modal", this._onModalHidden)
    this._teardownCropper()
    this._modal?.dispose()
  }

  openFilePicker(event) {
    event?.preventDefault()
    this.fileInputTarget.click()
  }

  handleFileChange(event) {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith("image/")) {
      this._resetFileInput()
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      this.cropperImageTarget.src = e.target.result
      this._modal.show()
      // Cropper needs the image element to be in the DOM and visible.
      this.modalTarget.addEventListener("shown.bs.modal", () => {
        this._initCropper()
      }, { once: true })
    }
    reader.readAsDataURL(file)
  }

  confirmCrop(event) {
    event?.preventDefault()
    if (!this._cropper) return

    const canvas = this._cropper.getCroppedCanvas({
      width: this.outputSizeValue,
      height: this.outputSizeValue,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high"
    })

    canvas.toBlob((blob) => {
      if (!blob) return
      const file = new File([blob], "avatar.jpg", { type: "image/jpeg" })
      const dt = new DataTransfer()
      dt.items.add(file)
      this.fileInputTarget.files = dt.files

      if (this.hasRemoveFlagTarget) {
        this.removeFlagTarget.value = "0"
      }

      this._setPreviewFromUrl(URL.createObjectURL(blob))
      this._showRemoveButton()
      this._modal.hide()
    }, "image/jpeg", 0.92)
  }

  cancelCrop(event) {
    event?.preventDefault()
    this._resetFileInput()
    this._modal.hide()
  }

  removeAvatar(event) {
    event?.preventDefault()
    this._resetFileInput()
    if (this.hasRemoveFlagTarget) {
      this.removeFlagTarget.value = "1"
    }
    this._setPreviewToInitials()
    this._hideRemoveButton()
  }

  _initCropper() {
    this._teardownCropper()
    this._cropper = new Cropper(this.cropperImageTarget, {
      aspectRatio: 1,
      viewMode: 1,
      autoCropArea: 1,
      background: false,
      movable: true,
      zoomable: true,
      rotatable: false,
      scalable: false,
      responsive: true,
      restore: false
    })
  }

  _teardownCropper() {
    if (this._cropper) {
      this._cropper.destroy()
      this._cropper = null
    }
  }

  _resetFileInput() {
    this.fileInputTarget.value = ""
  }

  _setPreviewFromUrl(url) {
    this.previewTarget.innerHTML = ""
    const img = document.createElement("img")
    img.src = url
    img.alt = "Avatar preview"
    img.className = "profile-avatar-img"
    this.previewTarget.appendChild(img)
  }

  _setPreviewToInitials() {
    this.previewTarget.innerHTML = ""
    const span = document.createElement("span")
    span.className = "profile-avatar-initials"
    span.textContent = this.initialsValue || ""
    this.previewTarget.appendChild(span)
  }

  _showRemoveButton() {
    if (this.hasRemoveBtnTarget) {
      this.removeBtnTarget.classList.remove("d-none")
    }
  }

  _hideRemoveButton() {
    if (this.hasRemoveBtnTarget) {
      this.removeBtnTarget.classList.add("d-none")
    }
  }
}
